import { CheckmarkFilled } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';

import { InteractiveAvatar } from 'bones/design-system/components/avatar/InteractiveAvatar';
import { Icon } from 'bones/design-system/components/icons/Icon';
import { VoiceboxLogo } from 'bones/design-system/components/icons/VoiceboxLogo';
import { Loading } from 'bones/design-system/components/Loading';
import { Body } from 'bones/design-system/components/typography/Body';

export interface VoiceboxChatIncomingProps {
  'data-testid'?: never;
  actions?: React.ReactNode;
  actionsRef?: React.Ref<HTMLDivElement>;
  avatarTestId?: string;
  badge?: React.ReactNode;
  content?: React.ReactNode;
  loading?: boolean;
  onSelect?: () => void;
  selectable?: boolean;
  selected?: boolean;
  showActions: boolean;
  slot?: React.ReactNode;
  testId?: string;
  title?: string;
}

export const VoiceboxChatIncoming: React.VFC<VoiceboxChatIncomingProps> = ({
  actions,
  actionsRef,
  avatarTestId,
  badge,
  content,
  loading,
  onSelect,
  selectable,
  selected,
  showActions,
  slot,
  testId,
  title,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);

  let avatarImage = (
    <Icon IconComponent={VoiceboxLogo} size="lg" theme="light" />
  );
  if (loading) {
    avatarImage = <Loading size="sm" />;
  } else if (selectable && isHovered) {
    avatarImage = <Icon IconComponent={CheckmarkFilled} size="md" />;
  }

  return (
    <div
      className={classNames('vb-voicebox-chat-incoming', {
        'vb-voicebox-chat-incoming--loading': loading,
        'vb-voicebox-chat-incoming--selected': selected,
      })}
      data-testid={testId}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={classNames('vb-voicebox-chat-indicator', {
          'vb-voicebox-chat-indicator--loading': loading,
          'vb-voicebox-chat-indicator--selectable': selectable,
          'vb-voicebox-chat-indicator--selected': selected,
        })}
      >
        <div className="vb-voicebox-chat-incoming-avatar">
          <InteractiveAvatar
            image={avatarImage}
            onClick={onSelect}
            size="md"
            testId={avatarTestId}
          />
        </div>

        <div className="vb-voicebox-chat-incoming-body">
          {badge}
          {title ? (
            /* istanbul ignore next */
            <Body
              className="vb-voicebox-chat-incoming-title"
              size="lg"
              weight="semibold"
            >
              {title}
            </Body>
          ) : null}
          {content ? (
            <Body className="vb-voicebox-chat-incoming-content" size="md">
              {content}
            </Body>
          ) : /* istanbul ignore next */ null}
          {slot}
        </div>

        <div ref={actionsRef} className="vb-voicebox-chat-incoming-actions">
          {showActions || isHovered ? actions : null}
        </div>
      </div>
    </div>
  );
};
